import { supabase } from '../utils/supabaseClient'

interface Person {
	number: number
	photo: string
	first_name: string
	second_name: string
	nick_name: string
	citizenship: string
	flag: string
	type: string
	role: string
	from_start: boolean
	captain: boolean
}

export interface TeamSquad {
	homeTeam: Person[]
	awayTeam: Person[]
}

interface Tournament {
	id: number
	type: string
	fk_parent: number | null
}


function formatDate(dateString:any) {
	const date = new Date(dateString);
	return date.toISOString().split('T')[0];
}

const fetchTeamSquads = async (matchId: number): Promise<TeamSquad> => {
	try {
		console.log('Fetching match data...')
		// Step 1: Fetch Match Data
		const { data: matchData, error: matchError } = await supabase
			.from('matches')
			.select(`
                id,
                fk_team_host,
                fk_team_guest,
                fk_tournament,
                tournaments (
                    id,
                    type,
                    fk_parent
                ),
                actual_start_time
            `)
			.eq('id', matchId)
			.single()

		if (matchError) {
			console.error('Match Error:', matchError)
			throw matchError
		}


		const { fk_team_host, fk_team_guest, fk_tournament, tournaments, actual_start_time } = matchData
		console.log('Match Data:', matchData)
		const formattedDate = formatDate(matchData.actual_start_time);
		console.log(formattedDate)

		// Step 2: Find the parent tournament of type "Tournament"
		const findParentTournament = async (initialTournament: Tournament): Promise<number> => {
			let currentTournament: Tournament | null = initialTournament
			while (currentTournament && currentTournament.type !== 'Tournament') {
				if (!currentTournament.fk_parent) {
					console.log('No parent tournament found, using current tournament')
					return currentTournament.id
				}
				const { data, error } = await supabase
					.from('tournaments')
					.select('id, type, fk_parent')
					.eq('id', currentTournament.fk_parent)
					.single()
				if (error) {
					console.error('Error fetching parent tournament:', error)
					throw error
				}
				currentTournament = data as Tournament | null
			}
			return currentTournament ? currentTournament.id : fk_tournament
		}

		const initialTournament: Tournament = Array.isArray(tournaments) ? tournaments[0] : tournaments
		const parentTournamentId = await findParentTournament(initialTournament)
		console.log('Parent Tournament ID:', parentTournamentId)

		// Step 3: Fetch Player Data for both teams
		const fetchTeamData = async (teamId: number) => {
			console.log(`Fetching data for team ${teamId}...`)

			// First, try to fetch from persona2team2season2tournament2match
			const { data: matchTeamData, error: matchTeamDataError } = await supabase
				.from('persona2team2season2tournament2match')
				.select(`
                    from_start,
                    captain,
                    fk_persona2team2season2tournament!inner (
                        fk_persona2team2season!inner (
                            fk_persona2team!inner (
                                fk_persona!inner (
                                    id,
                                    first_name,
                                    last_name,
                                    nick_name,
                                    persona2type (
                                        id,
                                        types (
                                            type
                                        ),
                                        role2persona2type (
                                            id,
                                            roles (
                                                id,
                                                role
                                            )
                                        )
                                    ),
                                    persona2country (
                                        countries (
                                            country,
                                            flag
                                        )
                                    )
                                ),
                                fk_team, 
                                number,
                                photo
                               
                            )
                        )
                    )
                `)
				.eq('fk_match', matchId)
				.eq('fk_persona2team2season2tournament.fk_persona2team2season.fk_persona2team.fk_team', teamId)
				console.log('С новыми условиями', matchTeamData)

			if (matchTeamDataError) {
				console.error(`Match Team Data Error (team ${teamId}):`, matchTeamDataError)
				throw matchTeamDataError
			}

			// If we got data from the first query, use it
			if (matchTeamData && matchTeamData.length > 0) {
				return matchTeamData.map(mapPersonData)
			}

			// If no data, fallback to persona2team2season2tournament
			console.log(`No match-specific data found for team ${teamId}, fetching from persona2team2season2tournament...`)
			const { data: tournamentTeamData, error: tournamentTeamDataError } = await supabase
				.from('persona2team2season2tournament')
				.select(`
                    fk_persona2team2season!inner (
                        fk_persona2team!inner (
                            fk_persona!inner (
                                id,
                                first_name,
                                last_name,
                                nick_name,
                                persona2type (
                                    id,
                                    types (
                                        type
                                    ),
                                    role2persona2type (
                                        id,
                                        roles (
                                            id,
                                            role
                                        )
                                    )
                                ),
                                persona2country (
                                    countries (
                                        country,
                                        flag
                                    )
                                )
                            ),
                            fk_team,
                            number,
                            photo
                           
                        )
                    )
                `)
				.eq('fk_tournament', parentTournamentId)
				.eq('fk_persona2team2season.fk_persona2team.fk_team', teamId)

			if (tournamentTeamDataError) {
				console.error(`Tournament Team Data Error (team ${teamId}):`, tournamentTeamDataError)
				throw tournamentTeamDataError
			}

			return tournamentTeamData.map(mapPersonData)
		}

		const mapPersonData = (entry: any): Person => ({
			number: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.number ||
				entry.fk_persona2team2season?.fk_persona2team?.number ||
				null,
			photo: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.photo ||
				entry.fk_persona2team2season?.fk_persona2team?.photo ||
				'',
			first_name: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.first_name ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.first_name ||
				'',
			second_name: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.last_name ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.last_name ||
				'',
			nick_name: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.nick_name ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.nick_name ||
				'',
			citizenship: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2country[0]?.countries?.country ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2country[0]?.countries?.country ||
				'',
			flag: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2country[0]?.countries?.flag ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2country[0]?.countries?.flag ||
				'',
			type: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2type[0]?.types?.type ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2type[0]?.types?.type ||
				'',
			role: entry.fk_persona2team2season2tournament?.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2type[0]?.role2persona2type[0]?.roles?.role ||
				entry.fk_persona2team2season?.fk_persona2team?.fk_persona?.persona2type[0]?.role2persona2type[0]?.roles?.role ||
				'',
			from_start: entry.from_start || false,
			captain: entry.captain || false,
		})

		const homeTeam = await fetchTeamData(fk_team_host)
		const awayTeam = await fetchTeamData(fk_team_guest)

		return { homeTeam, awayTeam }
	} catch (error) {
		console.error('Error fetching team squads:', error)
		throw error
	}
}

export default fetchTeamSquads